import { useGoBack } from 'hooks/useGoBack';
import React, { useCallback, useContext } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { EditDeviceContext, IEditDeviceContext } from 'modules/Devices/EditDevice/editDeviceState/editDeviceContext';
import { convertUndefinedFields } from 'utils/helpers';
import { editDevice } from 'backend/devices';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		changeTypeOfModalWindow,
		showErrorModalWithInfo,
		refetchData,
		dataForSubmit,
		typeOfModalWindow,
		errorResponseData,
	} = useContext<IEditDeviceContext>(EditDeviceContext);
	const goBack = useGoBack();

	const handleRedirect = useCallback(() => {
		goBack('/devices');
	}, [goBack]);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await editDevice(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.DEVICE_HARDWARE_ID_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
					changeTypeOfModalWindow(null);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleUpdateData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
		changeTypeOfModalWindow(null);
	}, []);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow || !!errorResponseData}>
			<ModalToConfirm
				onConsentEvent={handleRedirect}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleUpdateData}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'tlsFileSizeExceeded'}
				description="fileSizeExceeded"
				translationOptions={{ size: '1MB' }}
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'tlsFileTypeError'}
				description="invalidFileFormat"
				translationOptions={{ format: '.p12' }}
			/>
			{errorResponseData && (
				<ModalSentError
					onConsentEvent={handleCloseModalWithInfo}
					isVisible={true}
					description={errorResponseData.Type}
					translationOptions={{
						value: errorResponseData.Metadata.hardwareId,
					}}
				/>
			)}
		</ModalWrapper>
	);
};
